import * as React from 'react';
import { Checkbox, TextField, Autocomplete, CircularProgress } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { ChannelSelectionProps } from '../../../../../../../slices/orders/channelsSlice';
import { AxiosResponse } from 'axios';
import fetchClient from '../../../../../../../services/axios';
import { alertError } from '../../../../../../../slices/global/alertSlice';
import { useAppDispatch } from '../../../../../../../hooks';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export function capitalizeFirstLetter(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function formatChannelName(str: string) {
  return capitalizeFirstLetter(
    str
      .toLowerCase()
      .replaceAll('_', ' ')
      .replaceAll('.', ' ')
      .replaceAll('com', 'US')
      .replaceAll('us', 'US')
      .replaceAll('ca', 'CA')
      .replaceAll('motors', ''),
  );
}

interface Props {
  value: ChannelSelectionProps[] | undefined;
  handleValueChange: (newValue: ChannelSelectionProps[] | null) => void;
  label: string;
}

interface ResponseProps {
  id: number;
  name: string;
}

export default function ChannelsAutocomplete({ value, handleValueChange, label }: Props) {
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly ChannelSelectionProps[]>([]);
  const loading = open && options.length === 0;
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    const controller = new AbortController();
    let active = true;

    if (!loading) {
      return undefined;
    }

    (async () => {
      await fetchClient
        .get('/OrderFlow/Channels', { signal: controller.signal })
        .then((response: AxiosResponse<ResponseProps[]>) => {
          //Formatting label for channel's name
          let temp = response.data.map((item) => {
            return {
              id: item.id,
              name: item.name,
              label: formatChannelName(item.name),
            };
          });

          if (active) {
            setOptions([...temp.sort((x, y) => x.label.localeCompare(y.label, 'en'))]);
          }
        })
        .catch(function (err) {
          if (err.message !== 'canceled')
            dispatch(alertError(`Failed to retrieve channels. Reason: ${err}`));
        });
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [loading, dispatch]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  return (
    <Autocomplete
      multiple
      id="channels-autocomplete"
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      value={value}
      onChange={(_: any, newValue: ChannelSelectionProps[] | null) => {
        handleValueChange(newValue);
      }}
      loading={loading}
      options={options}
      disableCloseOnSelect
      isOptionEqualToValue={(option, value) => option.name === value.name}
      getOptionLabel={(option) => option.label}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            style={{ marginRight: 8 }}
            checked={selected}
          />
          {option.label}
        </li>
      )}
      limitTags={2}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          // error={error}
          // required
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      sx={{ minWidth: 300, maxWidth: 300 }}
    />
  );
}
